/**
 * Layout overrides — the one shared contract every configurable component
 * and the rule engine agree on.
 *
 * Pure functions — **no React imports, no Zod imports, no fetch**. This file
 * owns the serialisable {@link LayoutOverrides} shape (keyed by *stable*
 * field / column / section key, never by label) and the
 * {@link applyLayoutOverrides} merge that a migrated component applies to its
 * declarative config **before render**:
 *
 * ```ts
 * const config = useMemo(
 *   () => applyLayoutOverrides(baseConfig, layoutOverrides),
 *   [baseConfig, layoutOverrides],
 * );
 * ```
 *
 * The rule engine (WS1) produces a {@link LayoutOverrides} object from
 * `renameField` / `hideField` / `reorderFields` / … effects and injects it as
 * the `_layoutOverrides` prop. With no rules authored, `overrides` is
 * `undefined`/empty and every helper here returns the **input unchanged**
 * (referential identity) — zero visible change to any component.
 *
 * ## Author vocabulary vs. host property names
 *
 * Rule authors think in **one** vocabulary so they never have to know which
 * host they are targeting. The helpers below translate that vocabulary to
 * each host's real property:
 *
 * | Author concept | Field host (EntityForm) | Column host (DataList) | Info host (InfoList) |
 * | -------------- | ----------------------- | ---------------------- | -------------------- |
 * | show/hide      | `FieldConfig.visible`   | `ColumnConfig.hidden`  | item `visible`       |
 * | label          | `FieldConfig.label`     | `ColumnConfig.label`   | item `label`         |
 * | order          | `FieldConfig.order`     | `ColumnConfig.order`   | item `order`         |
 * | tooltip        | `FieldConfig.tooltip`   | `ColumnConfig.headerTooltip` / `cellTooltip` | item `tooltip` |
 *
 * Note the **visible/hidden polarity**: the field/info overrides use
 * `visible` and map straight through to a `visible` host property; the column
 * override deliberately uses `hidden` (matching `ColumnConfig.hidden`) so no
 * boolean inversion is ever needed — the author still reasons in "show/hide",
 * but the column bucket carries `hidden` directly to keep the merge a pure,
 * inversion-free shallow assignment.
 */

import type {
  ActionButtonConfig,
  ActionsConfig,
  CustomActionConfig,
  EntityFormConfig,
  FieldConfig,
  RuleFieldCondition,
  SectionConfig,
  SelectFieldOptions,
} from '@components/integrated/entity-form';
import type {
  ColumnConfig,
  DataListConfig,
  FilterConfig,
  SortState,
  ToolbarConfig,
} from '@components/integrated/data-list';

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/**
 * Default client-side error message applied when a `requireField` effect marks
 * a field required without supplying its own `message` (or supplies a blank
 * one). Lives in **one** place so the evaluator and any consumer agree on the
 * fallback text. The evaluator (`applyLayoutEffect`) folds this onto
 * {@link FieldOverride.requiredMessage}; the merge then maps it onto
 * `FieldConfig.validation.required`.
 */
export const DEFAULT_REQUIRED_MESSAGE = 'Ce champ est obligatoire';

// ---------------------------------------------------------------------------
// Override shapes (serialisable; keyed by stable key)
// ---------------------------------------------------------------------------

/**
 * Override for a single {@link FieldConfig} (EntityForm) or info-card item.
 * Authors reason in "show/hide" — `visible` maps straight onto the host's
 * own `visible` property (no inversion).
 */
export interface FieldOverride {
  /** New display label (i18n key or literal). */
  label?: string;
  /** New placeholder text (i18n key or literal). Maps onto the field's `placeholder`. */
  placeholder?: string;
  /** New helper text (i18n key or literal). Maps onto the field's `helperText`. */
  helperText?: string;
  /** New visibility. Maps directly onto the host's `visible` property. */
  visible?: boolean;
  /** New sort order (lower = first). Auto-render hosts sort by this. */
  order?: number;
  /** Tooltip text (i18n key or literal). Maps onto the field's `tooltip`. */
  tooltip?: string;
  /**
   * Explicit "remove the tooltip back to none" sentinel. A `true` value forces
   * the field's `tooltip` off even when the base config declares one — plain
   * key-removal in the override bucket can only *leave* a baseline tooltip
   * intact, never force it off (absence = "keep baseline"). Takes precedence
   * over a `tooltip` value if both are somehow present. See the layout-editor
   * free-text plan §1.1 rationale.
   */
  clearTooltip?: true;
  /**
   * Client-side **required** flag produced by the add-only `requireField`
   * effect. `true` marks the field required; `mergeFieldOverride` maps it onto
   * `FieldConfig.validation.required` (the asterisk + client validation). The
   * effect only ever sets `true`, but the merge handles `false` for
   * completeness.
   */
  required?: boolean;
  /**
   * Custom error message paired with {@link required} when the field is empty.
   * The evaluator defaults this to {@link DEFAULT_REQUIRED_MESSAGE} when the
   * `requireField` effect omits or blanks its `message`, so a populated
   * `required: true` always carries a message string. Mapped onto
   * `FieldConfig.validation.required` as the custom i18n key / literal.
   */
  requiredMessage?: string;
  /**
   * Rule-injected **runtime** conditions produced by `setFieldCondition`
   * effects targeting this field. Unlike every other slot on this interface
   * these are *append-only* — several effects may attach several conditions to
   * the same field, and they apply in effect order (last matching wins).
   * `mergeFieldOverride` appends them onto `FieldConfig.ruleConditions`, the
   * overlay layer EntityForm evaluates on top of the field's own conditions.
   */
  conditions?: RuleFieldCondition[];
}

/**
 * Override for a single {@link SectionConfig} (EntityForm sections array).
 * Only `label` and `order` are author-mutable; `label` maps onto the
 * section's display title (see note in {@link applySectionOverrides}).
 */
export interface SectionOverride {
  /** New section title (i18n key or literal). Maps onto `SectionConfig.title`. */
  label?: string;
  /** New sort order (lower = first). */
  order?: number;
}

/**
 * Override for a single {@link ColumnConfig} (DataList).
 *
 * Uses `hidden` (not `visible`) so it maps directly onto
 * `ColumnConfig.hidden` with no boolean inversion — the column host already
 * speaks "hidden", and DataList resolves columns "ordered, filtered by
 * hidden".
 */
export interface ColumnOverride {
  /** New header label (i18n key or literal). */
  label?: string;
  /** New hidden flag. Maps directly onto `ColumnConfig.hidden`. */
  hidden?: boolean;
  /** New sort order (lower = leftmost). */
  order?: number;
  /** Tooltip shown on hover of the column header. */
  headerTooltip?: string;
  /** Cell tooltip configuration (same shape as `ColumnConfig.cellTooltip`). */
  cellTooltip?: ColumnConfig['cellTooltip'];
  /**
   * Explicit "remove the header tooltip back to none" sentinel — symmetric to
   * {@link FieldOverride.clearTooltip}. Forces `ColumnConfig.headerTooltip` off
   * even when the base config declares one; takes precedence over a
   * `headerTooltip` value if both are present.
   */
  clearHeaderTooltip?: true;
  /**
   * Explicit "remove the cell tooltip back to none" sentinel. Forces
   * `ColumnConfig.cellTooltip` off even when the base config declares one;
   * takes precedence over a `cellTooltip` value if both are present.
   */
  clearCellTooltip?: true;
}

/**
 * Override for a single DataList toolbar {@link FilterConfig} (text / date /
 * select filter input), keyed in {@link LayoutOverrides.filters} by the stable
 * filter `id`.
 *
 * Uses `hidden` (not `visible`) so it maps directly onto `FilterConfig.hidden`
 * with no boolean inversion — symmetric with {@link ColumnOverride} (the toolbar
 * resolves filters "ordered, filtered by hidden", like columns).
 */
export interface FilterOverride {
  /** New filter label (i18n key or literal). Maps onto `FilterConfig.label`. */
  label?: string;
  /** New placeholder text (i18n key or literal). Maps onto `FilterConfig.placeholder`. */
  placeholder?: string;
  /** New hidden flag. Maps directly onto `FilterConfig.hidden`. */
  hidden?: boolean;
  /** New sort order (lower = leftmost). Maps onto `FilterConfig.order`. */
  order?: number;
}

/**
 * Override for the single DataList toolbar **search** input. Not a per-key
 * bucket — the toolbar has exactly one search box — so it lives as a single
 * value on {@link LayoutOverrides.search}. `hidden` resolves onto
 * `toolbar.search.enabled` (`enabled = !hidden`), since the toolbar gates the
 * search box on `enabled === true` and has no separate `hidden` flag.
 */
export interface SearchOverride {
  /** New placeholder text (i18n key or literal). Maps onto `toolbar.search.placeholder`. */
  placeholder?: string;
  /** New hidden flag. Resolves onto `toolbar.search.enabled = !hidden`. */
  hidden?: boolean;
}

/**
 * Override for a single action button (EntityForm `actions.submit` / `cancel` /
 * `reset`, or a `actions.custom[]` entry matched by `id`). Keyed in
 * {@link LayoutOverrides.actions} by the action key — `'submit'` / `'cancel'` /
 * `'reset'` map onto the named {@link ActionsConfig} fields; any other key maps
 * onto a matching `custom[].id`. DataList has no action buttons, so this is a
 * documented no-op on that host.
 */
export interface ActionOverride {
  /** New button label (i18n key or literal). Maps onto `ActionButtonConfig.label`. */
  label?: string;
  /** New visibility. Maps directly onto `ActionButtonConfig.visible`. */
  visible?: boolean;
}

/**
 * Per-tenant default-sort override for a {@link DataListConfig}. A single
 * `column` + `direction` pair (the common real request — "show oldest first",
 * "sort by amount") that maps onto the **first** entry of
 * `DataListConfig.sorting.defaultSort`. Unlike the field/column/section
 * buckets this is **not** a per-key map: a surface has exactly one initial
 * sort, so the last-write-wins fold collapses to a single value. `column` is
 * a stable column key. See `.claude-work/specs/data-row-ordering.md` (Tier 1).
 */
export interface DefaultSortOverride {
  /** Stable column key to sort by. */
  column: string;
  /** Sort direction. */
  direction: 'asc' | 'desc';
}

/**
 * The complete serialisable set of layout mutations a rule authors. Every
 * bucket is keyed by the **stable** field / column / section key — never the
 * label — because the key is the contract a rule targets and the label is
 * display-only and itself overridable.
 *
 * All buckets are optional; an empty or `undefined` object is an identity
 * (see {@link applyLayoutOverrides}).
 */
export interface LayoutOverrides {
  /** Overrides for `EntityFormConfig.fields`, keyed by field name. */
  fields?: Record<string, FieldOverride>;
  /** Overrides for `EntityFormConfig.sections`, keyed by section `id`. */
  sections?: Record<string, SectionOverride>;
  /** Overrides for `DataListConfig.columns`, keyed by column name. */
  columns?: Record<string, ColumnOverride>;
  /**
   * Overrides for `DataListConfig.toolbar.filters`, keyed by the stable filter
   * `id`. A DataList-host concern (a no-op on EntityForm). Carries the toolbar
   * filter inputs' label / placeholder / hidden / order. Empty/`undefined` ⇒
   * identity.
   */
  filters?: Record<string, FilterOverride>;
  /**
   * Override for the single DataList toolbar **search** input (placeholder /
   * hidden). Not a per-key bucket — one search box per toolbar — so it is a
   * single value, mirroring {@link defaultSort} / {@link title}. A DataList-host
   * concern; `undefined` ⇒ identity. See {@link SearchOverride}.
   */
  search?: SearchOverride;
  /**
   * Overrides for ordered info-card descriptors (the future `InfoList`),
   * keyed by item key. Same shape as {@link FieldOverride} — info items are
   * label/value pairs with the same author vocabulary as form fields.
   */
  items?: Record<string, FieldOverride>;
  /**
   * Per-tenant default sort for a DataList host. Maps onto
   * `DataListConfig.sorting.defaultSort` (the initial sort on first load).
   * Data-row order is not a per-key config property, so it lives here as a
   * single value rather than a keyed bucket. `undefined` ⇒ the host keeps its
   * own default sort (identity). See {@link DefaultSortOverride}.
   */
  defaultSort?: DefaultSortOverride;
  /**
   * Per-tenant **manual row order** for a DataList host (data-row-ordering
   * Tier 2). A list of **stable row ids** in the desired sequence. Like
   * {@link defaultSort} this is **not** a per-key map — a surface has exactly
   * one manual order — so the last-write-wins fold collapses to a single
   * array. It is the order of *records*, not a layout-config property, so it is
   * never merged into `columns`; instead the DataList host applies it with
   * {@link applyRowOrder} over its inline `data[]` before render (a documented
   * no-op for server-paginated sources). `undefined` ⇒ natural order
   * (identity). See `.claude-work/specs/data-row-ordering.md` (Tier 2).
   */
  rowOrder?: string[];
  /**
   * Per-tenant **component title** override. A single author-facing value that
   * maps to the right host property: EntityForm → `i18n.title`; DataList →
   * `i18n.title` (resolved by `DataListHeader` ahead of `display.title`). Not a
   * per-key bucket — a surface has one title — so the engine's fold collapses
   * it to a single last-write-wins value. `undefined` ⇒ the host keeps its own
   * title (identity). InfoList has no config title (the consuming card owns it).
   */
  title?: string;
  /**
   * Per-tenant **action-button** overrides, keyed by action key:
   * `'submit'` / `'cancel'` / `'reset'` map onto the named {@link ActionsConfig}
   * fields; any other key maps onto a matching `actions.custom[].id` entry.
   * Each value is an {@link ActionOverride} (`label` / `visible`). A no-op on
   * the DataList host (no action buttons). Empty/`undefined` ⇒ identity.
   */
  actions?: Record<string, ActionOverride>;
}

/**
 * Minimal shape an info-card item descriptor satisfies so it can consume
 * {@link LayoutOverrides.items}. The future shared `InfoList` component
 * (tracked separately by `ITEM-20260618-1108-info`) will render an
 * `Array<InfoItemDescriptor>`; this foundation only needs the override-able
 * surface so {@link applyItemOverrides} can be written and tested ahead of
 * that component. The full runtime descriptor additionally carries
 * `value`, `icon`, and `truncate`, which overrides never touch.
 */
export interface InfoItemDescriptor {
  /** Stable key the override targets (never the label). */
  key: string;
  /** Display label (i18n key or literal). */
  label?: string;
  /** Sort order (lower = first). */
  order?: number;
  /** Whether the item is visible. */
  visible?: boolean;
  /** Tooltip text (i18n key or literal). */
  tooltip?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Type guard — true when an overrides bucket is present and has at least one
 * key. Narrows the bucket to a defined `Record` so callers can index it
 * without a non-null assertion, and lets each host helper short-circuit the
 * empty/`undefined` case to a referential-identity return (provably
 * allocation-free and byte-identical).
 */
function isPopulatedBucket<V>(
  bucket: Record<string, V> | undefined,
): bucket is Record<string, V> {
  return bucket !== undefined && Object.keys(bucket).length > 0;
}

/**
 * True when a {@link SearchOverride} carries at least one effective key — the
 * single-value analogue of {@link isPopulatedBucket} for the (non-keyed) search
 * slot. An object with neither `placeholder` nor `hidden` is treated as empty so
 * the no-op identity path holds.
 */
function searchOverrideHasValue(
  search: SearchOverride | undefined,
): search is SearchOverride {
  return (
    search !== undefined &&
    (search.placeholder !== undefined || search.hidden !== undefined)
  );
}

/**
 * Merge a {@link FieldOverride} onto a {@link FieldConfig}, translating the
 * author vocabulary to the host property names. Only keys *present* on the
 * override are copied, so `undefined` override fields never clobber existing
 * config values. Returns a new object; never mutates `config`.
 */
function mergeFieldOverride(
  config: FieldConfig,
  override: FieldOverride,
): FieldConfig {
  const merged: FieldConfig = { ...config };
  if (override.label !== undefined) merged.label = override.label;
  if (override.placeholder !== undefined) {
    // SELECT / MULTISELECT read their placeholder from `options.placeholder`
    // (the select trigger), NOT the top-level `FieldConfig.placeholder` —
    // writing only the top-level slot would silently no-op for selects. Route
    // the override to the slot the host actually renders: `options.placeholder`
    // for selects, the top-level `placeholder` for text-like fields. (Both are
    // written harmlessly for selects so the editor's effective-value reader,
    // which reads its own draft effects, stays consistent.)
    merged.placeholder = override.placeholder;
    if (merged.type === 'select' || merged.type === 'multiselect') {
      const opts = merged.options as SelectFieldOptions | undefined;
      merged.options = { ...opts, placeholder: override.placeholder };
    }
  }
  if (override.helperText !== undefined) merged.helperText = override.helperText;
  if (override.visible !== undefined) merged.visible = override.visible;
  if (override.order !== undefined) merged.order = override.order;
  if (override.tooltip !== undefined) merged.tooltip = override.tooltip;
  // `clearTooltip` is the explicit "force off" sentinel and takes precedence
  // over any `tooltip` value set above — clearing wins so a baseline tooltip
  // can be removed even if the override carries both.
  if (override.clearTooltip === true) merged.tooltip = undefined;
  // `required` (the add-only `requireField` effect) maps onto
  // `FieldConfig.validation.required` (which `true` uses the default message,
  // a string carries the custom message). Clone `validation` first — it may be
  // `undefined` on the base config — so the base object is never mutated.
  if (override.required !== undefined) {
    const validation = { ...merged.validation };
    validation.required = override.required
      ? (override.requiredMessage ?? true)
      : false;
    merged.validation = validation;
  }
  // `conditions` (the `setFieldCondition` effect) is the one APPEND-only slot:
  // it stacks the rule-injected overlay entries onto any already present on the
  // config, in effect order. A fresh array is built every time, so neither the
  // base config's array nor the override's is ever mutated or aliased.
  if (override.conditions !== undefined && override.conditions.length > 0) {
    merged.ruleConditions = [
      ...(config.ruleConditions ?? []),
      ...override.conditions,
    ];
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Per-host helpers (one import site per host)
// ---------------------------------------------------------------------------

/**
 * Apply field overrides to an `EntityFormConfig.fields` map (or any
 * `Record<string, FieldConfig>`). Each override is shallow-merged onto the
 * config entry with the **same key**; keys present only in the override are
 * ignored (forward-compatible — a rule referencing a removed field is a
 * no-op, never a crash). Returns a new map; the input is never mutated.
 *
 * With an empty/`undefined` `overrides`, the input map is returned
 * unchanged (referential identity).
 */
export function applyFieldOverrides(
  fields: Record<string, FieldConfig>,
  overrides: Record<string, FieldOverride> | undefined,
): Record<string, FieldConfig> {
  if (!isPopulatedBucket(overrides)) return fields;

  let changed = false;
  const next: Record<string, FieldConfig> = {};
  for (const [key, field] of Object.entries(fields)) {
    const override = overrides[key];
    if (override) {
      next[key] = mergeFieldOverride(field, override);
      changed = true;
    } else {
      next[key] = field;
    }
  }
  // When the overrides only referenced unknown keys, nothing changed —
  // return the input map so the identity guarantee holds (forward-compatible
  // no-op for rules pointing at removed fields).
  return changed ? next : fields;
}

/**
 * Apply item overrides to an array of info-card descriptors. Same merge
 * semantics as {@link applyFieldOverrides} (shared {@link FieldOverride}
 * vocabulary), but keyed by each descriptor's `key` and operating over an
 * **array** (descriptors are ordered). Returns a new array; the input and
 * its elements are never mutated. Empty/`undefined` overrides ⇒ identity.
 */
export function applyItemOverrides<T extends InfoItemDescriptor>(
  items: readonly T[],
  overrides: Record<string, FieldOverride> | undefined,
): T[] | readonly T[] {
  if (!isPopulatedBucket(overrides)) return items;

  let changed = false;
  const next: T[] = [];
  for (const item of items) {
    const override = overrides[item.key];
    if (!override) {
      next.push(item);
      continue;
    }
    const merged: T = { ...item };
    if (override.label !== undefined) merged.label = override.label;
    if (override.visible !== undefined) merged.visible = override.visible;
    if (override.order !== undefined) merged.order = override.order;
    if (override.tooltip !== undefined) merged.tooltip = override.tooltip;
    // Info items have only label/visible/order/tooltip — `placeholder` /
    // `helperText` / `conditions` are not part of `InfoItemDescriptor` (an info
    // card has no form values to branch on), so they are ignored here.
    // `clearTooltip` forces the tooltip off (precedence over a value).
    if (override.clearTooltip === true) merged.tooltip = undefined;
    next.push(merged);
    changed = true;
  }
  return changed ? next : items;
}

/**
 * Apply column overrides to a `DataListConfig.columns` map (or any
 * `Record<string, ColumnConfig>`). `ColumnOverride.hidden` maps **directly**
 * onto `ColumnConfig.hidden` (no inversion — see the polarity note at the top
 * of this file). Keys present only in the override are ignored. Returns a new
 * map; the input is never mutated. Empty/`undefined` overrides ⇒ identity.
 */
export function applyColumnOverrides(
  columns: Record<string, ColumnConfig>,
  overrides: Record<string, ColumnOverride> | undefined,
): Record<string, ColumnConfig> {
  if (!isPopulatedBucket(overrides)) return columns;

  let changed = false;
  const next: Record<string, ColumnConfig> = {};
  for (const [key, column] of Object.entries(columns)) {
    const override = overrides[key];
    if (!override) {
      next[key] = column;
      continue;
    }
    const merged: ColumnConfig = { ...column };
    if (override.label !== undefined) merged.label = override.label;
    if (override.hidden !== undefined) merged.hidden = override.hidden;
    if (override.order !== undefined) merged.order = override.order;
    if (override.headerTooltip !== undefined) {
      merged.headerTooltip = override.headerTooltip;
    }
    if (override.cellTooltip !== undefined) {
      merged.cellTooltip = override.cellTooltip;
    }
    // Clear sentinels force the corresponding tooltip off, taking precedence
    // over any value set above so a baseline column tooltip can be removed.
    if (override.clearHeaderTooltip === true) merged.headerTooltip = undefined;
    if (override.clearCellTooltip === true) merged.cellTooltip = undefined;
    next[key] = merged;
    changed = true;
  }
  return changed ? next : columns;
}

/**
 * Apply filter overrides to a `DataListConfig.toolbar.filters` array. Filters
 * are an **array** of {@link FilterConfig} keyed by `.id`, so each override is
 * matched to the element whose `id` equals the override key (same shape as
 * {@link applySectionOverrides}). `FilterOverride.hidden` maps **directly** onto
 * `FilterConfig.hidden` (no inversion — the toolbar resolves filters "ordered,
 * filtered by hidden", like columns). Keys with no matching `id` are ignored
 * (forward-compatible no-op). Returns a new array; the input and its elements
 * are never mutated. Empty/`undefined` overrides ⇒ identity.
 */
export function applyFilterOverrides(
  filters: readonly FilterConfig[],
  overrides: Record<string, FilterOverride> | undefined,
): FilterConfig[] | readonly FilterConfig[] {
  if (!isPopulatedBucket(overrides)) return filters;

  let changed = false;
  const next: FilterConfig[] = [];
  for (const filter of filters) {
    const override = overrides[filter.id];
    if (!override) {
      next.push(filter);
      continue;
    }
    const merged: FilterConfig = { ...filter };
    if (override.label !== undefined) merged.label = override.label;
    if (override.placeholder !== undefined) {
      merged.placeholder = override.placeholder;
    }
    if (override.hidden !== undefined) merged.hidden = override.hidden;
    if (override.order !== undefined) merged.order = override.order;
    next.push(merged);
    changed = true;
  }
  return changed ? next : filters;
}

/**
 * Apply a {@link SearchOverride} onto a `ToolbarConfig.search` block. The author
 * speaks "placeholder / hidden"; `hidden` resolves onto the host's `enabled`
 * flag (`enabled = !hidden`) since the toolbar gates the search box on
 * `search.enabled === true` and has no separate `hidden` field. Only keys
 * present on the override are applied. Returns a new `ToolbarConfig`; the input
 * is never mutated. `undefined` override ⇒ identity.
 */
export function applySearchOverride(
  toolbar: ToolbarConfig | undefined,
  override: SearchOverride | undefined,
): ToolbarConfig | undefined {
  if (
    override === undefined ||
    (override.placeholder === undefined && override.hidden === undefined)
  ) {
    return toolbar;
  }
  const base = toolbar ?? {};
  const search: Partial<NonNullable<ToolbarConfig['search']>> = {
    ...base.search,
  };
  if (override.placeholder !== undefined) {
    search.placeholder = override.placeholder;
  }
  if (override.hidden !== undefined) {
    // The toolbar shows the search box when `enabled === true`; an override
    // `hidden` resolves to the inverse `enabled` flag.
    search.enabled = !override.hidden;
  }
  // `enabled` must always be a boolean once we touch the block; default a
  // placeholder-only override to enabled so the box stays visible.
  if (search.enabled === undefined) search.enabled = true;
  return { ...base, search: search as NonNullable<ToolbarConfig['search']> };
}

/**
 * Apply a {@link DefaultSortOverride} onto a `DataListConfig`'s sorting
 * config, returning a new config whose `sorting.defaultSort` is a single-entry
 * {@link SortState} array `[{ column, direction }]`. Any other `sorting`
 * settings (`enabled`, `mode`, param names) are preserved. The DataList sort
 * hook reads `config.sorting.defaultSort` to seed its initial `sortState`, so
 * this sets the tenant's initial row order.
 *
 * `undefined` override ⇒ the input config is returned unchanged (referential
 * identity). When an override is present a new config is always returned (a
 * tenant explicitly authored a default sort), so this helper does **not**
 * dedupe against a pre-existing identical default sort — the caller's
 * "a rule fired" path is the gate.
 *
 * Never mutates the input config or its nested `sorting` object.
 */
export function applyDefaultSortOverride<C extends DataListConfig>(
  config: C,
  override: DefaultSortOverride | undefined,
): C {
  if (override === undefined) return config;

  const defaultSort: SortState[] = [
    { column: override.column, direction: override.direction },
  ];
  return {
    ...config,
    sorting: { ...config.sorting, defaultSort },
  };
}

/**
 * Apply a manual {@link LayoutOverrides.rowOrder} to a fully-loaded array of
 * data rows (data-row-ordering Tier 2). Reorders `data` so the rows whose
 * stable id appears in `rowOrder` come **first**, in `rowOrder` sequence;
 * every other row keeps its **natural order** *after* the listed ones. Ids in
 * `rowOrder` that match no row are ignored; rows whose id is absent from
 * `rowOrder` are appended (so "new rows" added since the order was authored
 * still appear, at the end, in their natural order).
 *
 * The sort is **stable** (a single pass partitioning into listed-then-rest,
 * with the listed bucket placed by `rowOrder` index), so two rows with the
 * same listed rank — which cannot happen here since each id is listed once —
 * and all unlisted rows preserve their input order exactly.
 *
 * `getRowId` reads the stable id off a row (the ERP/business key — **never**
 * the array index). A row whose id is `undefined`/`null` is treated as
 * unlisted (kept in natural order at the end).
 *
 * Returns a **new** array when a reorder actually applies; for an
 * empty/`undefined` `rowOrder`, or empty `data`, the input array is returned
 * unchanged (referential identity — the no-rule path is allocation-free and
 * byte-identical). Never mutates the input array or its elements.
 *
 * Restricted by its callers to inline / fully-loaded `data[]` sources — see
 * the DataList integration, which makes manual order a documented no-op for
 * server-paginated data rather than misordering a single page.
 */
export function applyRowOrder<T>(
  data: readonly T[],
  rowOrder: readonly string[] | undefined,
  getRowId: (row: T) => string | undefined,
): T[] | readonly T[] {
  if (rowOrder === undefined || rowOrder.length === 0) return data;
  if (data.length === 0) return data;

  // Map each listed id to its rank (first occurrence wins, so a duplicate id
  // in `order` does not shuffle the second match to the back). Lower = first.
  const rank = new Map<string, number>();
  for (let i = 0; i < rowOrder.length; i += 1) {
    const id = rowOrder[i];
    if (id !== undefined && !rank.has(id)) rank.set(id, i);
  }

  const listed: Array<{ row: T; rank: number; index: number }> = [];
  const rest: T[] = [];

  data.forEach((row, index) => {
    const id = getRowId(row);
    const r = id === undefined ? undefined : rank.get(id);
    if (r === undefined) {
      rest.push(row);
    } else {
      listed.push({ row, rank: r, index });
    }
  });

  // No row matched any listed id ⇒ nothing to reorder ⇒ identity.
  if (listed.length === 0) return data;

  // Stable: order listed rows by their `order` rank, breaking ties (impossible
  // for distinct ids, but kept total) by original index so the sort never
  // reshuffles equal-rank rows. `rest` is already in natural order.
  listed.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.index - b.index));

  return [...listed.map((entry) => entry.row), ...rest];
}

/**
 * Apply section overrides to an `EntityFormConfig.sections` array. Sections
 * are an **array** of {@link SectionConfig} keyed by `.id`, so each override
 * is matched to the element whose `id` equals the override key.
 *
 * Naming note: the author vocabulary calls the section heading `label`, but
 * `SectionConfig` stores it as `title`. `SectionOverride.label` therefore
 * maps onto `SectionConfig.title` (mirroring how `EntityForm.Section`
 * renders the section heading). Keys with no matching `id` are ignored.
 *
 * Returns a new array; the input array and its elements are never mutated.
 * Empty/`undefined` overrides ⇒ identity.
 */
export function applySectionOverrides(
  sections: readonly SectionConfig[],
  overrides: Record<string, SectionOverride> | undefined,
): SectionConfig[] | readonly SectionConfig[] {
  if (!isPopulatedBucket(overrides)) return sections;

  let changed = false;
  const next: SectionConfig[] = [];
  for (const section of sections) {
    const override = overrides[section.id];
    if (!override) {
      next.push(section);
      continue;
    }
    const merged: SectionConfig = { ...section };
    if (override.label !== undefined) merged.title = override.label;
    if (override.order !== undefined) merged.order = override.order;
    next.push(merged);
    changed = true;
  }
  return changed ? next : sections;
}

// ---------------------------------------------------------------------------
// Combined, host-aware entry point
// ---------------------------------------------------------------------------

/**
 * Any declarative host config {@link applyLayoutOverrides} understands. The
 * helper detects which buckets apply by which keys the config carries
 * (`fields`/`sections` ⇒ EntityForm, `columns` ⇒ DataList), so a migrated
 * component can call a single entry point regardless of host.
 */
export type LayoutHostConfig = EntityFormConfig | DataListConfig;

/**
 * Apply a {@link LayoutOverrides.title} value onto a host config's `i18n.title`
 * slot. Both EntityForm (`EntityFormI18nOverrides.title`) and DataList
 * (`DataListI18nOverrides.title`, resolved by `DataListHeader` ahead of
 * `display.title`) carry the resolved title under `i18n.title`, so a single
 * author-facing `title` maps to the same property on either host. Returns a new
 * config; the input is never mutated. A `title` of `undefined` would never reach
 * here (the caller guards), so this always sets the slot.
 */
function applyTitleOverride<C extends LayoutHostConfig>(
  config: C,
  title: string,
): C {
  const withI18n = config as C & { i18n?: { title?: string } };
  return {
    ...config,
    i18n: { ...withI18n.i18n, title },
  } as C;
}

/**
 * Deep-merge keyed {@link ActionOverride}s onto an `EntityFormConfig.actions`
 * config. `'submit'` / `'cancel'` / `'reset'` map onto the named
 * {@link ActionsConfig} fields; any other key maps onto the matching
 * `actions.custom[]` entry whose `id` equals the key. For each, `label` →
 * `ActionButtonConfig.label` and `visible` → `ActionButtonConfig.visible`; only
 * keys present on the override are copied (`undefined` never clobbers). Keys
 * that match no named field and no `custom[].id` are ignored (forward-compatible
 * no-op). Returns a new `EntityFormConfig`; the input is never mutated. DataList
 * has no action buttons, so this is only reached for the EntityForm host.
 */
function applyActionOverrides(
  config: EntityFormConfig,
  actions: Record<string, ActionOverride>,
): EntityFormConfig {
  const named = new Set(['submit', 'cancel', 'reset']);
  const base: ActionsConfig = config.actions ?? {};
  const next: ActionsConfig = { ...base };
  let changed = false;

  const mergeButton = (
    button: ActionButtonConfig | undefined,
    override: ActionOverride,
  ): ActionButtonConfig => {
    const merged: ActionButtonConfig = { ...button };
    if (override.label !== undefined) merged.label = override.label;
    if (override.visible !== undefined) merged.visible = override.visible;
    return merged;
  };

  for (const [key, override] of Object.entries(actions)) {
    if (named.has(key)) {
      next[key as 'submit' | 'cancel' | 'reset'] = mergeButton(
        base[key as 'submit' | 'cancel' | 'reset'],
        override,
      );
      changed = true;
      continue;
    }
    // Custom action — match by `id` in the `custom[]` array.
    const custom = base.custom;
    if (custom === undefined) continue;
    const index = custom.findIndex((entry) => entry.id === key);
    if (index === -1) continue;
    const nextCustom: CustomActionConfig[] = [...custom];
    nextCustom[index] = {
      ...mergeButton(custom[index], override),
      id: custom[index]!.id,
      handler: custom[index]!.handler,
    } as CustomActionConfig;
    next.custom = nextCustom;
    changed = true;
  }

  return changed ? { ...config, actions: next } : config;
}

/**
 * Combined, host-aware merge. Dispatches each {@link LayoutOverrides} bucket
 * to the matching per-host helper based on the config's own shape:
 *
 * - `fields` / `sections` ⇒ {@link applyFieldOverrides} /
 *   {@link applySectionOverrides} on an {@link EntityFormConfig}.
 * - `columns` ⇒ {@link applyColumnOverrides} on a {@link DataListConfig}.
 *
 * Info-card descriptors are an array, not a config object, so they use
 * {@link applyItemOverrides} directly rather than this entry point.
 *
 * Returns a new config object only when at least one bucket actually changed
 * something; otherwise returns the **input** unchanged (referential
 * identity). Never mutates the input. Buckets that don't match the host shape
 * are ignored (e.g. `columns` on an EntityForm config is a no-op).
 *
 * @typeParam C - the concrete host config type, preserved on the return.
 */
export function applyLayoutOverrides<C extends LayoutHostConfig>(
  config: C,
  overrides: LayoutOverrides | undefined,
): C {
  if (
    overrides === undefined ||
    (!isPopulatedBucket(overrides.fields) &&
      !isPopulatedBucket(overrides.sections) &&
      !isPopulatedBucket(overrides.columns) &&
      !isPopulatedBucket(overrides.filters) &&
      !isPopulatedBucket(overrides.actions) &&
      !searchOverrideHasValue(overrides.search) &&
      overrides.title === undefined &&
      overrides.defaultSort === undefined &&
      (overrides.rowOrder === undefined || overrides.rowOrder.length === 0))
  ) {
    return config;
  }

  // Narrow on the concrete union (not the generic `C`, whose `in` narrowing
  // would only widen to `Record<'fields', unknown>`). The merged result is
  // cast back to `C` so the concrete host type is preserved on the return.
  const host: LayoutHostConfig = config;

  // EntityForm host — has a `fields` map.
  if ('fields' in host) {
    const nextFields = applyFieldOverrides(host.fields, overrides.fields);
    const nextSections =
      host.sections === undefined
        ? undefined
        : applySectionOverrides(host.sections, overrides.sections);

    // Title + action overrides are applied onto a working config so the
    // referential-identity check below sees them too.
    let next: EntityFormConfig = host;
    let changed = false;
    if (nextFields !== host.fields || nextSections !== host.sections) {
      // `nextSections` is the `readonly`-widened return of
      // `applySectionOverrides`; cast back to the config's mutable shape (the
      // array is never mutated downstream — this mirrors the original branch).
      next = {
        ...host,
        fields: nextFields,
        ...(nextSections === undefined ? {} : { sections: nextSections }),
      } as EntityFormConfig;
      changed = true;
    }
    if (overrides.title !== undefined) {
      next = applyTitleOverride(next, overrides.title);
      changed = true;
    }
    if (isPopulatedBucket(overrides.actions)) {
      const withActions = applyActionOverrides(next, overrides.actions);
      if (withActions !== next) {
        next = withActions;
        changed = true;
      }
    }

    // Referential identity when nothing changed anything.
    return changed ? (next as C) : config;
  }

  // DataList host — has a `columns` map. The column overrides, the
  // default-sort override, and the manual row order all target this host;
  // apply each, then short-circuit to referential identity only when none
  // changed anything. The row order is stamped onto `dataSource.rowOrder`
  // (data order, not a config-merge) so every DataList card that already calls
  // `applyLayoutOverrides(config, _layoutOverrides)` inherits the tenant's
  // manual order without a per-card edit; `useDataList` reads it in client
  // mode (and degrades for server-paginated sources).
  const nextColumns = applyColumnOverrides(host.columns, overrides.columns);
  const columnsChanged = nextColumns !== host.columns;
  const sortChanged = overrides.defaultSort !== undefined;
  const rowOrderChanged =
    overrides.rowOrder !== undefined && overrides.rowOrder.length > 0;
  // Title maps onto `i18n.title` (resolved by `DataListHeader` ahead of
  // `display.title`). `overrides.actions` is a no-op on DataList (no action
  // buttons) and is intentionally not consulted here.
  const titleChanged = overrides.title !== undefined;

  // Toolbar: the filter inputs (`toolbar.filters[]`, keyed by id) and the single
  // search box. Build a new toolbar only when an override actually changes
  // something so the identity guarantee holds.
  let nextToolbar: ToolbarConfig | undefined = host.toolbar;
  let toolbarChanged = false;
  if (host.toolbar?.filters !== undefined) {
    const nextFilters = applyFilterOverrides(
      host.toolbar.filters,
      overrides.filters,
    );
    if (nextFilters !== host.toolbar.filters) {
      nextToolbar = { ...(nextToolbar ?? {}), filters: nextFilters as FilterConfig[] };
      toolbarChanged = true;
    }
  }
  const afterSearch = applySearchOverride(nextToolbar, overrides.search);
  if (afterSearch !== nextToolbar) {
    nextToolbar = afterSearch;
    toolbarChanged = true;
  }

  if (
    !columnsChanged &&
    !sortChanged &&
    !rowOrderChanged &&
    !titleChanged &&
    !toolbarChanged
  ) {
    return config;
  }

  let next: DataListConfig = host;
  if (columnsChanged) {
    next = { ...next, columns: nextColumns };
  }
  if (toolbarChanged) {
    next = { ...next, toolbar: nextToolbar };
  }
  if (sortChanged) {
    next = applyDefaultSortOverride(next, overrides.defaultSort);
  }
  if (rowOrderChanged) {
    next = {
      ...next,
      dataSource: { ...next.dataSource, rowOrder: overrides.rowOrder },
    };
  }
  if (titleChanged) {
    next = applyTitleOverride(next, overrides.title as string);
  }
  return next as C;
}
